import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { formatInTimeZone } from 'date-fns-tz';
import {
  AUTHORIZATION_KIND_LABEL,
  AUTHORIZATION_STATUS_LABEL,
  createAuthorization,
  DuplicatePendingAuthorizationError,
  getEvidenceUrl,
  listMyAuthorizations,
  type AuthorizationKind,
  type AuthorizationRow,
} from '../../api/authorizations';
import { rechazoDeArchivo } from '../../lib/archivoDeFoto';

const TZ = 'America/Lima';
const KINDS: AuthorizationKind[] = ['tardanza', 'falta', 'permiso'];

const STATUS_CLS: Record<string, string> = {
  pendiente: 'bg-amber-100 text-amber-800',
  aprobado: 'bg-green-100 text-green-800',
  rechazado: 'bg-red-100 text-red-800',
};

const dmy = (d: string) => d.split('-').reverse().join('/');

export default function MiSolicitud() {
  const hoy = formatInTimeZone(new Date(), TZ, 'yyyy-MM-dd');
  const [kind, setKind] = useState<AuthorizationKind>('tardanza');
  const [eventDate, setEventDate] = useState(hoy);
  const [reason, setReason] = useState('');
  const [evidence, setEvidence] = useState<File | null>(null);
  const [fileKey, setFileKey] = useState(0);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [ok, setOk] = useState('');
  const [rows, setRows] = useState<AuthorizationRow[]>([]);
  const [loading, setLoading] = useState(true);

  const recargar = useCallback(() => {
    setLoading(true);
    listMyAuthorizations()
      .then(setRows)
      .catch(() => setError('No pudimos cargar tus solicitudes. Revisa tu conexión.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(recargar, [recargar]);

  async function enviar(e: FormEvent) {
    e.preventDefault();
    setError('');
    setOk('');
    if (reason.trim() === '') {
      setError('Escribe el motivo de tu solicitud.');
      return;
    }
    setSending(true);
    try {
      await createAuthorization({ kind, eventDate, reason, evidence });
      setOk('Tu solicitud fue enviada. Tu coordinador la revisará.');
      setReason('');
      setEvidence(null);
      setFileKey((k) => k + 1);
      recargar();
    } catch (err) {
      if (err instanceof DuplicatePendingAuthorizationError) setError(err.message);
      else setError(err instanceof Error ? err.message : 'No se pudo enviar tu solicitud.');
    } finally {
      setSending(false);
    }
  }

  async function verEvidencia(path: string) {
    try {
      window.open(await getEvidenceUrl(path), '_blank');
    } catch {
      setError('No se pudo abrir la foto.');
    }
  }

  return (
    <div>
      <h1 className="mb-1 text-lg font-bold">Justificar tardanza o falta</h1>
      <p className="mb-4 text-sm text-gray-600">
        Cuéntanos qué pasó. Tu coordinador revisará la solicitud.
      </p>

      <form onSubmit={enviar} className="mb-6 rounded bg-white p-4 shadow">
        <label className="mb-3 block text-sm font-medium">
          Tipo
          <select
            className="mt-1 w-full rounded border px-3 py-2 text-base"
            value={kind}
            onChange={(e) => setKind(e.target.value as AuthorizationKind)}
          >
            {KINDS.map((k) => (
              <option key={k} value={k}>
                {AUTHORIZATION_KIND_LABEL[k]}
              </option>
            ))}
          </select>
        </label>

        <label className="mb-3 block text-sm font-medium">
          Fecha del evento
          <input
            type="date"
            className="mt-1 w-full rounded border px-3 py-2 text-base"
            value={eventDate}
            onChange={(e) => setEventDate(e.target.value)}
          />
        </label>

        <label className="mb-3 block text-sm font-medium">
          Motivo
          <textarea
            className="mt-1 w-full rounded border px-3 py-2 text-base"
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Ej.: Cita médica en el hospital, adjunto la constancia."
          />
        </label>

        <label className="mb-4 block text-sm font-medium">
          Foto de evidencia (opcional)
          <input
            key={fileKey}
            type="file"
            accept="image/*"
            capture="environment"
            className="mt-1 w-full text-sm"
            onChange={(e) => {
              // Tipo y tope del bucket (0314), sobre el original: la evidencia se comprime recién
              // al enviar y, si el navegador no puede, viaja tal cual.
              const f = e.target.files?.[0] ?? null;
              const motivo = f === null ? null : rechazoDeArchivo(f);
              if (motivo !== null) {
                setError(motivo);
                setEvidence(null);
                e.target.value = '';
                return;
              }
              setError('');
              setEvidence(f);
            }}
          />
        </label>

        {error && <p className="mb-2 text-sm text-red-600">{error}</p>}
        {ok && <p className="mb-2 text-sm text-green-700">{ok}</p>}

        <button
          type="submit"
          disabled={sending}
          className="w-full rounded bg-blue-600 py-3 text-base font-medium text-white disabled:opacity-50"
        >
          {sending ? 'Enviando…' : 'Enviar solicitud'}
        </button>
      </form>

      <h2 className="mb-2 text-base font-bold">Mis solicitudes</h2>
      {loading && <p className="text-sm text-gray-500">Cargando…</p>}
      {!loading && rows.length === 0 && (
        <p className="text-sm text-gray-500">Todavía no enviaste ninguna solicitud.</p>
      )}
      <ul className="space-y-2">
        {rows.map((r) => (
          <li key={r.id} className="rounded bg-white p-3 shadow">
            <div className="flex items-center justify-between">
              <span className="font-medium">
                {AUTHORIZATION_KIND_LABEL[r.kind]} — {dmy(r.event_date)}
              </span>
              <span className={`rounded px-2 py-0.5 text-xs ${STATUS_CLS[r.status]}`}>
                {AUTHORIZATION_STATUS_LABEL[r.status]}
              </span>
            </div>
            <p className="mt-1 text-sm text-gray-700">{r.reason}</p>
            {r.review_note && (
              <p className="mt-1 text-sm text-gray-600">
                <span className="font-medium">Comentario del revisor:</span> {r.review_note}
              </p>
            )}
            {r.evidence_path && (
              <button
                onClick={() => verEvidencia(r.evidence_path!)}
                className="mt-1 text-sm text-blue-600 underline"
              >
                Ver mi foto
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
